import { type ChangeEvent, useCallback, useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { withMask } from 'use-mask-input'

import { TextInput, TextInputProps } from '@/components/ui/text-input'

export type TextInputFormFieldProps = TextInputProps & {
  name: string
  withRestore?: boolean
  clearable?: boolean
  helperText?: string
  mask?: Parameters<typeof withMask>[0]
  maskOptions?: Parameters<typeof withMask>[1]
}

export const TextInputFormField = ({
  name,
  withRestore,
  clearable,
  mask,
  maskOptions,
  disabled,
  maxLength,
  ...props
}: TextInputFormFieldProps) => {
  const { field, fieldState } = useController({ name })
  const { resetField, clearErrors, trigger } = useFormContext()
  const applyMaskRef = useMemo(
    () => (mask ? withMask(mask, maskOptions) : undefined),
    [mask, maskOptions],
  )

  const handleInputRef = useCallback(
    (input: HTMLInputElement | null) => {
      field.ref(input)
      applyMaskRef?.(input)
    },
    [applyMaskRef, field],
  )

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    field.onChange(e)
    if (!e.target.value) clearErrors(name)
    // Character limit is validated out-of-scope of the onTouched flow: show the
    // error immediately on overflow without waiting for the field to be touched.
    else if (maxLength && e.target.value.length > maxLength) trigger(name)
  }

  const handleRestore = () => {
    resetField(name)
  }

  return (
    <TextInput
      ref={handleInputRef}
      error={fieldState.error?.message}
      name={field.name}
      value={field.value ?? ''}
      disabled={disabled}
      clearable={!disabled && clearable}
      onBlur={field.onBlur}
      onChange={handleChange}
      onRestore={!disabled && withRestore ? handleRestore : undefined}
      {...props}
    />
  )
}
